import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { Plus } from "lucide-react";
import {
  barStyle,
  buildGanttDays,
  formatMonthLabel,
  GANTT_DAY_WIDTH_PX,
  GANTT_LABEL_WIDTH_PX,
  GANTT_LOAD_MORE_WEEKS,
  GANTT_ROW_HEIGHT_PX,
  GANTT_SCROLL_EDGE_PX,
  GANTT_WEEK_DAYS,
  type GanttBar,
  type GanttDay,
} from "@/lib/pitchPlannerGantt";
import type { PitchPlannerRow } from "@/lib/pitchPlanner";
import { addDaysLocal, todayLocal } from "@/lib/bookingDates";
import {
  isPlannerEventsLabel,
  PLANNER_EVENTS_LABEL,
  type RegionHoliday,
} from "@/lib/stayCalendarNotes";
import { categoryColorOrDefault } from "@/lib/pitchCategoryColors";
import { isPitchFixed } from "@/lib/pitchAssignment";
import type { Booking, PlannerCustomEntry, PlannerCustomRow } from "@/types/camping";
import { resolveGuestName } from "@/lib/guestDisplay";
import "./planner.css";

const PAN_THRESHOLD_PX = 5;
const EDGE_LOAD_COOLDOWN_MS = 600;

export interface PitchPlannerGanttHandle {
  scrollByDays: (days: number) => void;
  scrollToToday: () => void;
}

export interface PitchPlannerGanttChartProps {
  rows: PitchPlannerRow[];
  customEntries: PlannerCustomEntry[];
  customRows: PlannerCustomRow[];
  holidays?: RegionHoliday[] | null;
  holidayPlaceLabel?: string | null;
  rangeFrom: string;
  rangeTo: string;
  loadingMore?: boolean;
  refreshing?: boolean;
  selectedBookingId?: string | null;
  selectedCustomEntryId?: string | null;
  onOpenBooking: (booking: Booking) => void;
  onEditBooking?: (booking: Booking) => void;
  onOpenCustomEntry?: (entry: PlannerCustomEntry) => void;
  onEditCustomEntry?: (entry: PlannerCustomEntry) => void;
  onAddCustomEntry?: (rowLabel: string, startDate: string) => void;
  onClearSelection?: () => void;
  onLoadPast?: () => void;
  onLoadFuture?: () => void;
  /** Balkenansicht füllt die verfügbare Höhe und scrollt darin. */
  fillHeight?: boolean;
}

interface GanttChartRow {
  id: string;
  label: string;
  sublabel?: string;
  categoryColor?: string | null;
  bars: GanttBar[];
  isCustom?: boolean;
  railColor?: string;
  calendarRow?: boolean;
}

interface ContextMenuState {
  x: number;
  y: number;
  rowLabel: string;
  startDate: string;
}

interface PanDrag {
  pointerId: number;
  startX: number;
  startY: number;
  scrollLeft: number;
  scrollTop: number;
  moved: boolean;
}

function dayHeaderClass(day: GanttDay): string {
  return clsx(
    "pp-head-day",
    day.isToday && "pp-today-head",
    !day.isToday && day.isHoliday && "pp-holiday",
    !day.isToday && !day.isHoliday && day.isWeekend && "pp-weekend",
  );
}

function dayCellClass(day: GanttDay): string {
  return clsx(
    "pp-daycell",
    day.isToday && "pp-today",
    !day.isToday && day.isHoliday && "pp-holiday",
    !day.isToday && !day.isHoliday && day.isWeekend && "pp-weekend",
  );
}

function isMonthCurrent(monthKey: string, todayIso: string): boolean {
  const today = todayIso.slice(0, 7);
  return monthKey === today;
}

/** Eindeutig pro Balken, auch wenn dieselbe Buchung auf mehreren Plätzen läge. */
function occupiedBarKey(bookingId: string, pitchCode: string, from: string, to: string): string {
  return `${bookingId}@${pitchCode}@${from}@${to}`;
}

function segmentToBar(
  segment: PitchPlannerRow["segments"][number],
  pitchCode: string,
  guestName: string,
  categoryColor: string | null | undefined,
): GanttBar | null {
  if (segment.kind === "free") return null;
  if (segment.kind === "blocked_pitch") {
    return {
      id: `blocked-${pitchCode}-${segment.from}`,
      from: segment.from,
      to: segment.to,
      label: "Gesperrt",
      kind: "blocked_pitch",
    };
  }
  const booking = segment.booking;
  return {
    id: booking
      ? occupiedBarKey(booking.id, pitchCode, segment.from, segment.to)
      : `occupied-${pitchCode}-${segment.from}`,
    from: segment.from,
    to: segment.to,
    label: guestName,
    sublabel: booking?.booking_number,
    kind: "occupied",
    color: categoryColorOrDefault(categoryColor),
    isFixed: booking ? isPitchFixed(booking.pitch_assignment_mode) : false,
    bookingId: booking?.id,
  };
}

function buildChartRows(
  plannerRows: PitchPlannerRow[],
  customEntries: PlannerCustomEntry[],
  customRows: PlannerCustomRow[],
  holidays: RegionHoliday[] | null | undefined,
  holidayPlaceLabel: string | null | undefined,
): GanttChartRow[] {
  const pitchRows: GanttChartRow[] = plannerRows.map(({ pitch, segments }) => {
    const bars = segments
      .map((segment) => {
        const guestName = segment.booking
          ? resolveGuestName(segment.booking.guest_id, { booking: segment.booking })
          : "";
        return segmentToBar(segment, pitch.code, guestName, pitch.category_color);
      })
      .filter((bar): bar is GanttBar => bar != null);
    return {
      id: pitch.id,
      label: pitch.code,
      sublabel: pitch.label ?? pitch.category_name ?? undefined,
      categoryColor: pitch.category_color,
      bars,
    };
  });

  const customRowsMap = new Map<string, GanttChartRow>();
  const sortedCustomRows = [...customRows].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.label.localeCompare(b.label, "de");
  });
  for (const row of sortedCustomRows) {
    if (isPlannerEventsLabel(row.label)) continue;
    const rowKey = row.label.trim().toLowerCase();
    customRowsMap.set(rowKey, {
      id: row.id,
      label: row.label,
      bars: [],
      isCustom: true,
    });
  }

  const eventBars: GanttBar[] = [];
  for (const entry of customEntries) {
    if (isPlannerEventsLabel(entry.label)) {
      eventBars.push({
        id: entry.id,
        from: entry.start_date,
        to: entry.end_date,
        label: entry.title,
        kind: "custom",
        color: entry.color ?? "#6366f1",
        customEntryId: entry.id,
      });
      continue;
    }
    const rowKey = entry.label.trim().toLowerCase();
    const bar: GanttBar = {
      id: entry.id,
      from: entry.start_date,
      to: entry.end_date,
      label: entry.title,
      kind: "custom",
      color: entry.color ?? undefined,
      customEntryId: entry.id,
    };
    const existing = customRowsMap.get(rowKey);
    if (existing) {
      existing.bars.push(bar);
    } else {
      customRowsMap.set(rowKey, {
        id: `custom-${entry.id}`,
        label: entry.label,
        bars: [bar],
        isCustom: true,
      });
    }
  }

  const eventRow: GanttChartRow = {
    id: "planner-events",
    label: PLANNER_EVENTS_LABEL,
    sublabel: eventBars.length === 0 ? "Eintragen" : undefined,
    bars: eventBars,
    isCustom: true,
    calendarRow: true,
    railColor: "#6366f1",
  };
  const holidayBars: GanttBar[] = (holidays ?? []).map((holiday) => ({
    id: `holiday-${holiday.date}`,
    from: holiday.date,
    to: addDaysLocal(holiday.date, 1),
    label: holiday.name,
    kind: "holiday",
    color: "#f59e0b",
  }));
  const holidayRow: GanttChartRow = {
    id: "planner-holidays",
    label: "Feiertage",
    sublabel: holidayPlaceLabel ?? "wird ermittelt",
    bars: holidayBars,
    calendarRow: true,
    railColor: "#f59e0b",
  };

  return [eventRow, holidayRow, ...pitchRows, ...Array.from(customRowsMap.values())];
}

function PlannerLegend() {
  return (
    <div className="pp-legend">
      <span className="pp-legend-item">
        <span className="pp-legend-today" aria-hidden />
        Heute
      </span>
      <span className="pp-legend-item">
        <span className="pp-legend-weekend" aria-hidden />
        Wochenende
      </span>
      <span className="pp-legend-item">
        <span className="pp-legend-holiday" aria-hidden />
        Feiertag
      </span>
      <span className="pp-legend-item">
        <span className="pp-legend-fixed" aria-hidden>
          !
        </span>
        Fester Platz
      </span>
      <span className="pp-legend-item">
        <span className="pp-legend-blocked" aria-hidden />
        Gesperrt
      </span>
    </div>
  );
}

function GanttHeader({ days, todayIso }: { days: GanttDay[]; todayIso: string }) {
  const monthSpans = useMemo(() => {
    const spans: { key: string; label: string; count: number }[] = [];
    for (const day of days) {
      const last = spans[spans.length - 1];
      if (last?.key === day.monthKey) {
        last.count += 1;
      } else {
        spans.push({ key: day.monthKey, label: formatMonthLabel(day.monthKey), count: 1 });
      }
    }
    return spans;
  }, [days]);

  return (
    <div className="pp-header sticky top-0 z-20 flex">
      <div
        className="pp-corner sticky left-0 z-30 shrink-0"
        style={{ width: GANTT_LABEL_WIDTH_PX }}
      >
        <span className="pp-corner-label">Plätze</span>
      </div>
      <div>
        <div className="flex">
          {monthSpans.map((span) => (
            <div
              key={span.key}
              className={clsx("pp-month-cell", isMonthCurrent(span.key, todayIso) && "pp-month-current")}
              style={{ width: span.count * GANTT_DAY_WIDTH_PX }}
            >
              {span.label}
            </div>
          ))}
        </div>
        <div className="pp-head-days flex">
          {days.map((day) => (
            <div
              key={day.iso}
              className={dayHeaderClass(day)}
              style={{ width: GANTT_DAY_WIDTH_PX }}
              title={day.holidayName ?? day.iso}
            >
              <div className="pp-head-weekday">{day.weekday.replace(/\.$/, "")}</div>
              <div className="pp-head-day-num">{day.dayOfMonth}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const GanttRow = memo(function GanttRow({
  row,
  days,
  bookingById,
  customById,
  selectedBookingId,
  selectedCustomEntryId,
  onOpenBooking,
  onEditBooking,
  onOpenCustomEntry,
  onEditCustomEntry,
  onContextMenu,
  onAddCustomEntry,
}: {
  row: GanttChartRow;
  days: GanttDay[];
  bookingById: Map<string, Booking>;
  customById: Map<string, PlannerCustomEntry>;
  selectedBookingId?: string | null;
  selectedCustomEntryId?: string | null;
  onOpenBooking: (booking: Booking) => void;
  onEditBooking?: (booking: Booking) => void;
  onOpenCustomEntry?: (entry: PlannerCustomEntry) => void;
  onEditCustomEntry?: (entry: PlannerCustomEntry) => void;
  onContextMenu?: (event: React.MouseEvent, rowLabel: string, dayIndex: number) => void;
  onAddCustomEntry?: (rowLabel: string, startDate: string) => void;
}) {
  const rowSelected = row.bars.some(
    (bar) =>
      (bar.bookingId != null && bar.bookingId === selectedBookingId) ||
      (bar.customEntryId != null && bar.customEntryId === selectedCustomEntryId),
  );

  return (
    <div
      className={clsx("pp-row flex", row.calendarRow && "pp-row-calendar", rowSelected && "pp-row-selected")}
      style={{ minHeight: GANTT_ROW_HEIGHT_PX }}
    >
      <div
        className="pp-label sticky left-0 z-10 shrink-0"
        style={{ width: GANTT_LABEL_WIDTH_PX }}
      >
        <span
          className="pp-label-rail"
          style={{
            backgroundColor: row.railColor
              ? row.railColor
              : row.categoryColor
                ? categoryColorOrDefault(row.categoryColor)
                : row.isCustom
                  ? "var(--eh-accent)"
                  : "var(--eh-fill)",
          }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <span className="pp-label-code">{row.label}</span>
          {row.sublabel ? <span className="pp-label-sub">{row.sublabel}</span> : null}
        </div>
        {row.id === "planner-events" && onAddCustomEntry ? (
          <button
            type="button"
            className="pp-row-add"
            aria-label="Veranstaltung hinzufügen"
            onClick={() => {
              const start = days.find((day) => day.isToday)?.iso ?? days[0]?.iso;
              if (start) onAddCustomEntry(row.label, start);
            }}
          >
            <Plus size={14} />
          </button>
        ) : null}
      </div>
      <div
        className="pp-track relative"
        style={{ width: days.length * GANTT_DAY_WIDTH_PX }}
        onContextMenu={
          row.isCustom && onContextMenu
            ? (event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                const offsetX = event.clientX - rect.left;
                const dayIndex = Math.min(
                  days.length - 1,
                  Math.max(0, Math.floor(offsetX / GANTT_DAY_WIDTH_PX)),
                );
                onContextMenu(event, row.label, dayIndex);
              }
            : undefined
        }
      >
        {row.bars.map((bar) => {
          const style = barStyle(days, bar.from, bar.to);
          if (!style) return null;
          const barColor =
            bar.kind === "occupied"
              ? categoryColorOrDefault(bar.color)
              : bar.kind === "blocked_pitch"
                ? undefined
                : bar.color;
          const selected =
            (bar.bookingId != null && bar.bookingId === selectedBookingId) ||
            (bar.customEntryId != null && bar.customEntryId === selectedCustomEntryId);
          const interactive = Boolean(bar.bookingId || bar.customEntryId);
          return (
            <button
              key={bar.id}
              type="button"
              className={clsx(
                "pp-bar max-w-full",
                bar.isFixed && "pp-bar-fixed",
                selected && "pp-bar-selected",
                bar.kind === "blocked_pitch" && "pp-bar-blocked",
                bar.kind === "holiday" && "pp-bar-holiday",
                bar.kind === "custom" && !barColor && "pp-bar-custom",
                !interactive && "pp-bar-static",
              )}
              style={{
                left: style.left + 3,
                width: Math.max(style.width - 6, 12),
                top: 7,
                height: GANTT_ROW_HEIGHT_PX - 14,
                backgroundColor:
                  bar.kind === "blocked_pitch"
                    ? undefined
                    : bar.kind === "custom" && !barColor
                      ? undefined
                      : barColor,
              }}
              title={`${bar.isFixed ? "! " : ""}${bar.label}${bar.sublabel ? ` · ${bar.sublabel}` : ""}`}
              aria-pressed={interactive ? selected : undefined}
              onClick={() => {
                if (bar.bookingId) {
                  const booking = bookingById.get(bar.id);
                  if (booking) onOpenBooking(booking);
                  return;
                }
                if (bar.customEntryId) {
                  const entry = customById.get(bar.customEntryId);
                  if (!entry) return;
                  if (onOpenCustomEntry) onOpenCustomEntry(entry);
                  else onEditCustomEntry?.(entry);
                }
              }}
              onDoubleClick={() => {
                if (bar.bookingId && onEditBooking) {
                  const booking = bookingById.get(bar.id);
                  if (booking) onEditBooking(booking);
                  return;
                }
                if (bar.customEntryId && onEditCustomEntry) {
                  const entry = customById.get(bar.customEntryId);
                  if (entry) onEditCustomEntry(entry);
                }
              }}
            >
              <span className="pp-bar-label truncate">
                {bar.label}
                {bar.sublabel ? <span className="opacity-80"> · {bar.sublabel}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

function GanttBackdrop({ days, todayIdx }: { days: GanttDay[]; todayIdx: number }) {
  const dayLine = "color-mix(in srgb, var(--eh-separator, rgba(60, 60, 67, 0.29)) 45%, transparent)";
  return (
    <div
      className="pp-backdrop"
      style={{
        left: GANTT_LABEL_WIDTH_PX,
        width: days.length * GANTT_DAY_WIDTH_PX,
        backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent ${GANTT_DAY_WIDTH_PX - 1}px, ${dayLine} ${GANTT_DAY_WIDTH_PX - 1}px, ${dayLine} ${GANTT_DAY_WIDTH_PX}px)`,
      }}
      aria-hidden
    >
      {days.map((day, index) =>
        !day.isToday && (day.isWeekend || day.isHoliday) ? (
          <div
            key={day.iso}
            className={dayCellClass(day)}
            style={{ left: index * GANTT_DAY_WIDTH_PX, width: GANTT_DAY_WIDTH_PX }}
          />
        ) : null,
      )}
      {todayIdx >= 0 ? (
        <div
          className="pp-today-marker"
          style={{ left: todayIdx * GANTT_DAY_WIDTH_PX, width: GANTT_DAY_WIDTH_PX }}
        />
      ) : null}
    </div>
  );
}

export const PitchPlannerGanttChart = forwardRef<PitchPlannerGanttHandle, PitchPlannerGanttChartProps>(
  function PitchPlannerGanttChart(
    {
      rows,
      customEntries,
      customRows,
      holidays = null,
      holidayPlaceLabel = null,
      rangeFrom,
      rangeTo,
      loadingMore = false,
      refreshing = false,
      selectedBookingId = null,
      selectedCustomEntryId = null,
      onOpenBooking,
      onEditBooking,
      onOpenCustomEntry,
      onEditCustomEntry,
      onAddCustomEntry,
      onClearSelection,
      onLoadPast,
      onLoadFuture,
      fillHeight = false,
    },
    ref,
  ) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const pendingScrollAdjustRef = useRef<number | null>(null);
    const pendingNudgeRef = useRef(0);
    const pendingFutureNudgeRef = useRef(0);
    const loadingPastRef = useRef(false);
    const loadingFutureRef = useRef(false);
    const centeredOnTodayRef = useRef(false);
    const programmaticScrollRef = useRef(false);
    const edgeCooldownUntilRef = useRef(0);
    const suppressClickRef = useRef(false);
    const panCleanupRef = useRef<(() => void) | null>(null);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [panning, setPanning] = useState(false);

    const todayIso = todayLocal();
    const holidayNames = useMemo(() => {
      if (!holidays) return null;
      return new Map(holidays.map((holiday) => [holiday.date, holiday.name]));
    }, [holidays]);
    const days = useMemo(
      () => buildGanttDays(rangeFrom, rangeTo, todayIso, holidayNames),
      [holidayNames, rangeFrom, rangeTo, todayIso],
    );
    const todayIdx = useMemo(() => days.findIndex((day) => day.isToday), [days]);
    const chartRows = useMemo(
      () => buildChartRows(rows, customEntries, customRows, holidays, holidayPlaceLabel),
      [customEntries, customRows, holidayPlaceLabel, holidays, rows],
    );

    const bookingById = useMemo(() => {
      const map = new Map<string, Booking>();
      for (const { pitch, segments } of rows) {
        for (const segment of segments) {
          if (!segment.booking) continue;
          map.set(
            occupiedBarKey(segment.booking.id, pitch.code, segment.from, segment.to),
            segment.booking,
          );
        }
      }
      return map;
    }, [rows]);

    const customById = useMemo(
      () => new Map(customEntries.map((entry) => [entry.id, entry])),
      [customEntries],
    );

    const gridWidth = days.length * GANTT_DAY_WIDTH_PX + GANTT_LABEL_WIDTH_PX;
    const loadMoreWidth = GANTT_LOAD_MORE_WEEKS * GANTT_WEEK_DAYS * GANTT_DAY_WIDTH_PX;

    const scrollToToday = useCallback(() => {
      const el = scrollRef.current;
      if (!el || todayIdx < 0) return;
      const dayCenter = todayIdx * GANTT_DAY_WIDTH_PX + GANTT_DAY_WIDTH_PX / 2;
      const visibleTrackWidth = Math.max(0, el.clientWidth - GANTT_LABEL_WIDTH_PX);
      programmaticScrollRef.current = true;
      edgeCooldownUntilRef.current = Date.now() + EDGE_LOAD_COOLDOWN_MS;
      el.scrollTo({ left: Math.max(0, dayCenter - visibleTrackWidth / 2), behavior: "smooth" });
    }, [todayIdx]);

    const scrollByDays = useCallback(
      (dayCount: number) => {
        const el = scrollRef.current;
        if (!el || dayCount === 0) return;
        const delta = dayCount * GANTT_DAY_WIDTH_PX;
        if (
          dayCount < 0 &&
          el.scrollLeft + delta < 1 &&
          onLoadPast &&
          !loadingPastRef.current &&
          !loadingMore
        ) {
          loadingPastRef.current = true;
          pendingScrollAdjustRef.current = loadMoreWidth;
          pendingNudgeRef.current += delta;
          onLoadPast();
          return;
        }
        const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
        if (
          dayCount > 0 &&
          delta > remaining + 1 &&
          onLoadFuture &&
          !loadingFutureRef.current &&
          !loadingMore
        ) {
          loadingFutureRef.current = true;
          pendingFutureNudgeRef.current += delta;
          onLoadFuture();
          return;
        }
        programmaticScrollRef.current = true;
        edgeCooldownUntilRef.current = Date.now() + EDGE_LOAD_COOLDOWN_MS;
        el.scrollBy({ left: delta, behavior: "smooth" });
      },
      [loadMoreWidth, loadingMore, onLoadFuture, onLoadPast],
    );

    useImperativeHandle(
      ref,
      () => ({
        scrollByDays,
        scrollToToday,
      }),
      [scrollByDays, scrollToToday],
    );

    useLayoutEffect(() => {
      if (centeredOnTodayRef.current || todayIdx < 0) return;
      const el = scrollRef.current;
      if (!el) return;
      const dayCenter = todayIdx * GANTT_DAY_WIDTH_PX + GANTT_DAY_WIDTH_PX / 2;
      const visibleTrackWidth = Math.max(0, el.clientWidth - GANTT_LABEL_WIDTH_PX);
      const nextLeft = Math.max(0, dayCenter - visibleTrackWidth / 2);
      if (el.scrollLeft !== nextLeft) {
        programmaticScrollRef.current = true;
        el.scrollLeft = nextLeft;
      }
      centeredOnTodayRef.current = true;
    }, [todayIdx, days.length]);

    useLayoutEffect(() => {
      const container = scrollRef.current;
      if (!container || pendingScrollAdjustRef.current == null) return;
      const delta = pendingScrollAdjustRef.current;
      const nudge = pendingNudgeRef.current;
      pendingScrollAdjustRef.current = null;
      pendingNudgeRef.current = 0;
      const nextLeft = container.scrollLeft + delta + nudge;
      if (container.scrollLeft !== nextLeft) {
        programmaticScrollRef.current = true;
        container.scrollLeft = nextLeft;
      }
    }, [rangeFrom, days.length]);

    useLayoutEffect(() => {
      const nudge = pendingFutureNudgeRef.current;
      const container = scrollRef.current;
      if (!nudge || !container) return;
      pendingFutureNudgeRef.current = 0;
      const nextLeft = container.scrollLeft + nudge;
      if (container.scrollLeft !== nextLeft) {
        programmaticScrollRef.current = true;
        container.scrollLeft = nextLeft;
      }
    }, [rangeTo, days.length]);

    const handleScroll = useCallback(() => {
      if (programmaticScrollRef.current) {
        programmaticScrollRef.current = false;
        return;
      }
      const container = scrollRef.current;
      if (!container || loadingMore) return;
      if (Date.now() < edgeCooldownUntilRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      // Passt die Zeitleiste in den Ausschnitt, bleibt scrollLeft bei 0. Die linke
      // Kante gilt dann dauerhaft als erreicht und würde Woche um Woche nachladen.
      if (scrollWidth <= clientWidth + 1) return;

      if (onLoadPast && scrollLeft < GANTT_SCROLL_EDGE_PX && !loadingPastRef.current) {
        loadingPastRef.current = true;
        pendingScrollAdjustRef.current = loadMoreWidth;
        edgeCooldownUntilRef.current = Date.now() + EDGE_LOAD_COOLDOWN_MS;
        onLoadPast();
      } else if (
        onLoadFuture &&
        scrollWidth - scrollLeft - clientWidth < GANTT_SCROLL_EDGE_PX &&
        !loadingFutureRef.current
      ) {
        loadingFutureRef.current = true;
        edgeCooldownUntilRef.current = Date.now() + EDGE_LOAD_COOLDOWN_MS;
        onLoadFuture();
      }
    }, [loadMoreWidth, loadingMore, onLoadFuture, onLoadPast]);

    useEffect(() => {
      if (!loadingMore) {
        loadingPastRef.current = false;
        loadingFutureRef.current = false;
        edgeCooldownUntilRef.current = Math.max(
          edgeCooldownUntilRef.current,
          Date.now() + EDGE_LOAD_COOLDOWN_MS,
        );
      }
    }, [loadingMore]);

    useEffect(() => () => panCleanupRef.current?.(), []);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const onWheel = (event: WheelEvent) => {
        const horizontal =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
        if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
          event.preventDefault();
          el.scrollLeft += event.deltaY;
        }
        if (
          horizontal < -1 &&
          el.scrollLeft <= 0 &&
          onLoadPast &&
          !loadingPastRef.current &&
          !loadingMore &&
          Date.now() >= edgeCooldownUntilRef.current
        ) {
          loadingPastRef.current = true;
          pendingScrollAdjustRef.current = loadMoreWidth;
          edgeCooldownUntilRef.current = Date.now() + EDGE_LOAD_COOLDOWN_MS;
          onLoadPast();
        }
      };
      el.addEventListener("wheel", onWheel, { passive: false });
      return () => el.removeEventListener("wheel", onWheel);
    }, [loadMoreWidth, loadingMore, onLoadPast]);

    useEffect(() => {
      if (!contextMenu) return;
      const close = () => setContextMenu(null);
      window.addEventListener("click", close);
      window.addEventListener("scroll", close, true);
      return () => {
        window.removeEventListener("click", close);
        window.removeEventListener("scroll", close, true);
      };
    }, [contextMenu]);

    const handleRowContextMenu = useCallback(
      (event: React.MouseEvent, rowLabel: string, dayIndex: number) => {
        if (!onAddCustomEntry) return;
        event.preventDefault();
        const startDate = days[dayIndex]?.iso;
        if (!startDate) return;
        setContextMenu({
          x: event.clientX,
          y: event.clientY,
          rowLabel,
          startDate,
        });
      },
      [days, onAddCustomEntry],
    );

    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || event.pointerType === "touch") return;
      const target = event.target as HTMLElement;
      if (target.closest("input, select, textarea, a, button")) return;
      const el = scrollRef.current;
      if (!el) return;
      const drag: PanDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop,
        moved: false,
      };

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        const dx = ev.clientX - drag.startX;
        const dy = ev.clientY - drag.startY;
        if (!drag.moved) {
          if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return;
          drag.moved = true;
          setPanning(true);
        }
        const node = scrollRef.current;
        if (!node) return;
        node.scrollLeft = drag.scrollLeft - dx;
        node.scrollTop = drag.scrollTop - dy;
      };
      const end = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
        panCleanupRef.current = null;
        if (drag.moved) {
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
        }
        setPanning(false);
      };
      panCleanupRef.current = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", end);
      window.addEventListener("pointercancel", end);
    }, []);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollByDays(-1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollByDays(1);
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          scrollRef.current?.scrollBy({ top: -GANTT_ROW_HEIGHT_PX, behavior: "smooth" });
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          scrollRef.current?.scrollBy({ top: GANTT_ROW_HEIGHT_PX, behavior: "smooth" });
        } else if (event.key === "Home") {
          event.preventDefault();
          scrollToToday();
        }
      },
      [scrollByDays, scrollToToday],
    );

    return (
      <div
        className={clsx(
          "relative flex min-h-0 min-w-0 flex-col",
          fillHeight ? "h-full min-h-0 flex-1" : "max-h-[min(78dvh,calc(100dvh-12rem))]",
        )}
      >
        <div className="pp-shell">
          <div className="pp-legend-bar">
            <PlannerLegend />
            {loadingMore || refreshing ? (
              <span className="shrink-0 text-xs text-label-tertiary">
                {loadingMore ? "Weitere Wochen…" : "Aktualisiere…"}
              </span>
            ) : null}
          </div>
          <div
            ref={scrollRef}
            className={clsx(
              "pp-viewport min-h-0 flex-1 overflow-auto overscroll-contain",
              panning && "is-panning",
            )}
            tabIndex={0}
            role="region"
            aria-label="Belegungsplan. Ziehen zum Verschieben, Pfeiltasten zum Bewegen, Einträge anklicken für Details."
            onScroll={handleScroll}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            onClickCapture={(event) => {
              if (suppressClickRef.current) {
                event.preventDefault();
                event.stopPropagation();
                suppressClickRef.current = false;
                return;
              }
              const target = event.target as HTMLElement;
              if (target.closest("button, a")) return;
              if (target.closest(".pp-track")) onClearSelection?.();
            }}
          >
            <div style={{ minWidth: gridWidth }}>
              <GanttHeader days={days} todayIso={todayIso} />
              {chartRows.length === 0 ? (
                <p className="planner-empty">Keine Stellplätze für die Filterauswahl.</p>
              ) : (
                <div className="pp-body">
                  <GanttBackdrop days={days} todayIdx={todayIdx} />
                  {chartRows.map((row) => (
                    <GanttRow
                      key={row.id}
                      row={row}
                      days={days}
                      bookingById={bookingById}
                      customById={customById}
                      selectedBookingId={selectedBookingId}
                      selectedCustomEntryId={selectedCustomEntryId}
                      onOpenBooking={onOpenBooking}
                      onEditBooking={onEditBooking}
                      onOpenCustomEntry={onOpenCustomEntry}
                      onEditCustomEntry={onEditCustomEntry}
                      onContextMenu={onAddCustomEntry ? handleRowContextMenu : undefined}
                      onAddCustomEntry={onAddCustomEntry}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {contextMenu ? (
          <div
            className="pp-context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                onAddCustomEntry?.(contextMenu.rowLabel, contextMenu.startDate);
                setContextMenu(null);
              }}
            >
              {isPlannerEventsLabel(contextMenu.rowLabel) ? "Veranstaltung hinzufügen" : "Eintrag hinzufügen"}
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);
